import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { HeatLinkButton } from "@/shared/data-entries/HeatLinkButton";
import { GradeSelector } from "@/shared/selectors/GradeSelector";
import { HeatSelector } from "@/shared/selectors/HeatSelector";
import { formatProductionDate } from "@/utils/date-time-formatter";
import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import {
  Box,
  Button,
  Card,
  Checkbox,
  FormControlLabel,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { NotificationSnackBar } from "../../../controls/snackbars/notification-snackbar";
import { RefreshButtons } from "../../../shared/data-entries/RefreshButtons";
import { StatusTitle } from "../../../shared/data-entries/StatusTitle";
import { NotesFieldDE } from "../../../shared/data-entries/common/NotesFieldDE";
import { ProcessDataDateTimeFieldDE } from "../../../shared/data-entries/process-data/ProcessDataDateTimeFieldDE";
import { ProcessDataNumberTextField } from "../../../shared/data-entries/process-data/ProcessDataNumberTextField";
import { ProcessDataTextFieldDE } from "../../../shared/data-entries/process-data/ProcessDataTextFieldDE";
import { LadleSelector } from "../../../shared/selectors/LadleSelector";
import { OperatorSelector } from "../../../shared/selectors/OperatorSelector";
import DurationInput from "@/shared/data-entries/common/DurationInput";
import { TableChemistry } from "../../laboratory/data-entry/components/tables/chemistries/TableChemistry";
import { ProcessNavigator } from "@/shared/data-entries/ProcessNavigator";
import {
  getLastestProcesses,
  searchProcesses,
} from "./repositories/lmf.repositories";
import { ArriveButtons } from "./components/buttons/ArriveButtons";
import { SteelTemperatureChart } from "./components/charts/SteelTemperatureChart";
import { LinkLMFProcess } from "./components/modals/LinkLMFDE";
import { SelectLadleCarModal } from "./components/modals/SelectLadleCarModal";
import { ShipAlertModal } from "@/controls/ag-grid/components/ShipAlertModal";
import { TableAdditions } from "./components/tables//table-additions/TableAdditions";
import { TableHeatings } from "./components/tables/table-heatings/TableHeatings";
import { TablePlugMetrics } from "./components/tables/table-plug-metrics/TablePlugMetrics";
import { TableProbes } from "./components/tables/table-probes/TableProbes";
import {
  readAllPlugsFromPLC,
  readProcessDataFromPLC,
} from "./repositories/lmf.repositories";
import { useDataEntryContext } from "./context/reducer.provider";
import { MissingFieldsBanner } from "./components/MissingFieldsBanner";
import { useTimerContext } from "./context/timer.provider";
import { validateTimeField } from "./helpers/de-helper";
import { useLMFDataEntryActions } from "./hooks/useLMFDataEntryActions";
import { useLinkUnlinkProcess } from "./hooks/useLinkUnlinkProcess";
import { Process } from "./models/lmf.models";
import { LADLE_CAR_COLORS } from "./constants/ladleCarColors";
import { dataEntryStyles } from "../../../shared/styles/dataEntryStyles";

type FormValues = {
  Start: Date | null;
  End: Date | null;
};

export const LMFDataEntryPage = () => {
  const theme = useTheme();
  const styles = dataEntryStyles(theme);

  const navigate = useNavigate();
  const { seconds, isRunning, pause, restart, resume } = useTimerContext();

  const {
    saveHeat,
    saveProcess,
    saveProcessData,
    refreshData,
    changeHeatStatus,
    loadData,
  } = useLMFDataEntryActions();
  const params = useParams();
  const {
    state: {
      deProcess,
      notificationMessage,
      isDataLoading,
      errorMessage,
      lastUpdate,
    },
    actions: { setProcess, setNotificationMessage, setErrorMessage },
  } = useDataEntryContext();
  const [showArrive, setShowArrive] = useState(false);
  const [showLadleCarModal, setShowLadleCarModal] = useState(false);
  const [showShipAlert, setShowShipAlert] = useState(false);
  const [dismissedProcessId, setDismissedProcessId] = useState<number | null>(
    null,
  );

  const process = deProcess?.process;
  const missingFieldNames = new Set(
    deProcess?.missingFields?.map((f) => f.FieldName) ?? [],
  );

  const { control, reset } = useForm<FormValues>({
    mode: "onChange",
  });

  useEffect(() => {
    if (process) {
      reset({
        Start: process?.Start,
        End: process?.End,
      });
    }
  }, [process?.ProcessId, setProcess]);

  useEffect(() => {
    if (
      process?.PendingShipSignalTime &&
      process?.End === null &&
      process?.ProcessId !== dismissedProcessId
    ) {
      setShowShipAlert(true);
    }
  }, [
    process?.PendingShipSignalTime,
    process?.ProcessId,
    process?.End,
    dismissedProcessId,
  ]);

  const {
    handleLinkHeat,
    handleUnlinkHeat,
    showLinkModal,
    showUnlinkModal,
    unlinkProcess,
    setShowLinkModal,
  } = useLinkUnlinkProcess(process || ({} as Process));

  return (
    <>
      <HelmetDexteel
        title={`${
          process?.HeatName ? `${process.HeatName} - ` : ""
        }LMF Data Entry`}
      />
      {deProcess?.missingFields && deProcess.missingFields.length > 0 && (
        <MissingFieldsBanner missingFields={deProcess.missingFields} />
      )}
      <Box
        padding={2}
        style={{ height: "calc(100vh - 64px)", paddingBottom: "60px" }}
      >
        <Grid container spacing={1} direction={"column"}>
          <Grid item>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              LMF Data Entry
            </Typography>
          </Grid>
          <Grid item>
            <Paper>
              <Box padding={1}>
                <Grid
                  container
                  justifyContent={"space-between"}
                  alignItems={"flex-start"}
                  spacing={3}
                >
                  <Grid item xs={12} md={2}>
                    <Grid container direction="column" spacing={1}>
                      <Grid item>
                        <ProcessNavigator
                          key={`lmf-de-process-navigator`}
                          process={deProcess?.process ?? null}
                          loadData={loadData}
                          getLatestProcesses={getLastestProcesses}
                          searchProcesses={searchProcesses}
                          pause={pause}
                          resume={resume}
                          onError={setErrorMessage}
                          initialProcessId={
                            params.processId
                              ? Number(params.processId)
                              : undefined
                          }
                        />
                      </Grid>
                      <Grid item>
                        <StatusTitle
                          aditionalLabel={
                            process?.TreatmentNumber != null
                              ? `Treatment ${process.TreatmentNumber}`
                              : undefined
                          }
                        />
                        {process?.LadleCar != null ? (
                          <Grid item sx={{ mt: 1 }}>
                            <Card
                              variant="outlined"
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                userSelect: "none",
                                height: "40px",
                                backgroundColor:
                                  process?.LadleCar === 1
                                    ? LADLE_CAR_COLORS.lc1.bg
                                    : LADLE_CAR_COLORS.lc2.bg,
                                borderColor:
                                  process?.LadleCar === 1
                                    ? LADLE_CAR_COLORS.lc1.border
                                    : LADLE_CAR_COLORS.lc2.border,
                                ...(process?.IsArriveManual && {
                                  cursor: "pointer",
                                  "&:hover": {
                                    backgroundColor:
                                      process?.LadleCar === 1
                                        ? LADLE_CAR_COLORS.lc1.hover
                                        : LADLE_CAR_COLORS.lc2.hover,
                                  },
                                }),
                              }}
                              onClick={() => {
                                if (process?.IsArriveManual) {
                                  setShowLadleCarModal(true);
                                }
                              }}
                            >
                              <Typography>
                                {`Ladle Car ${process?.LadleCar}`}
                              </Typography>
                            </Card>
                          </Grid>
                        ) : (
                          <Grid item sx={{ mt: 1 }}>
                            <Button
                              variant="contained"
                              color="primary"
                              fullWidth
                              sx={{
                                height: "40px",
                                textTransform: "none",
                                fontSize: "1rem",
                                fontWeight: 400,
                                letterSpacing: "0.00938em",
                                lineHeight: 1.5,
                              }}
                              onClick={() => setShowLadleCarModal(true)}
                            >
                              Assign Ladle Car
                            </Button>
                          </Grid>
                        )}
                        <Grid
                          container
                          style={{
                            display: "flex",
                            alignItems: "center",
                            marginTop: "8px",
                          }}
                        >
                          <Grid item md={10}>
                            <StatusTitle
                              heatStatus={process?.HeatStatusCode ?? ""}
                            />
                          </Grid>
                          <Grid item md={2}>
                            <HeatLinkButton
                              isLinked={process?.HeatId != null}
                              disabled={!process}
                              onLink={() => {
                                pause();
                                if (process) {
                                  handleLinkHeat();
                                }
                              }}
                              onUnlink={async () => {
                                pause();
                                if (process) {
                                  await unlinkProcess();
                                }
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid
                    key={`lmf-data-entry_first-${process?.ProcessId}`}
                    item
                    xs={12}
                    sm={6}
                    md={2}
                  >
                    <Grid container direction={"column"} spacing={1}>
                      <Grid item>
                        <TextField
                          fullWidth
                          label={"Shift"}
                          variant={"outlined"}
                          disabled
                          value={`${formatProductionDate(process?.ProductionDate)} ${process?.Shift} - ${process?.Crew}`}
                        />
                      </Grid>
                      <Grid item>
                        <Controller
                          name="Start"
                          control={control}
                          rules={{
                            required: "Arrive time is required",
                            validate: (value) =>
                              validateTimeField(value, "Start", process),
                          }}
                          render={({ field, fieldState: { error } }) => (
                            <>
                              <ProcessDataDateTimeFieldDE<Process>
                                value={field.value}
                                field={"Start"}
                                label="Arrive"
                                format="MM-DD-YYYY HH:mm"
                                clearable={false}
                                onChange={(value) => {
                                  field.onChange(value);
                                  setProcess({ ...process, Start: value });
                                }}
                                onBlur={(value) => {
                                  if (!error) {
                                    const updatedProcessForSave = {
                                      ...process,
                                      Start: value,
                                      AssetId: process?.AssetId ?? 0,
                                      ProcessId: process?.ProcessId ?? 0,
                                    };

                                    saveProcess(
                                      updatedProcessForSave as Process,
                                    ).then(() => resume());
                                  }
                                }}
                                onFocus={pause}
                              />
                              {error && (
                                <Typography variant="caption" color="error">
                                  {error.message}
                                </Typography>
                              )}
                            </>
                          )}
                        />
                      </Grid>
                      <Grid item>
                        <Controller
                          name="End"
                          control={control}
                          rules={{
                            required: "Ship time is required",
                            validate: (value) =>
                              validateTimeField(value, "End", process),
                          }}
                          render={({ field, fieldState: { error } }) => (
                            <>
                              <ProcessDataDateTimeFieldDE<Process>
                                value={field.value}
                                field={"End"}
                                label="Ship"
                                format="MM-DD-YYYY HH:mm"
                                clearable={false}
                                onChange={(value) => {
                                  field.onChange(value);
                                  setProcess({ ...process, End: value });
                                }}
                                onBlur={(value) => {
                                  if (!error) {
                                    const updatedProcessForSave = {
                                      ...process,
                                      End: value,
                                      AssetId: process?.AssetId ?? 0,
                                      ProcessId: process?.ProcessId ?? 0,
                                    };

                                    saveProcess(
                                      updatedProcessForSave as Process,
                                    ).then(() => resume());
                                  }
                                }}
                                onFocus={pause}
                              />
                              {error && (
                                <Typography variant="caption" color="error">
                                  {error.message}
                                </Typography>
                              )}
                            </>
                          )}
                        />
                      </Grid>
                      <Grid item>
                        <TextField
                          fullWidth
                          label={"Treatment Duration [min]"}
                          variant={"outlined"}
                          disabled
                          value={
                            process?.ProcessTime ? process?.ProcessTime : 0
                          }
                          onChange={(e) => {
                            setProcess({
                              ...process,
                              ProcessTime: Number(e.target.value),
                            });
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid
                    key={`lmf-data-entry_sec-${process?.ProcessId}`}
                    item
                    xs={12}
                    sm={6}
                    md={2}
                  >
                    <Grid container direction={"column"} spacing={1}>
                      <Grid item>
                        <GradeSelector
                          disableClearable
                          disabled={!process?.HeatId}
                          value={process?.GradeId ?? null}
                          highlight={missingFieldNames.has("GradeId")}
                          onChange={(value: number | null) => {
                            if (process?.HeatId) {
                              saveHeat({
                                heatId: process.HeatId,
                                field: "GradeId",
                                value,
                              });
                            }
                          }}
                          onFocus={pause}
                          onBlur={resume}
                        />
                      </Grid>
                      <Grid item>
                        <OperatorSelector
                          disableClearable
                          value={process?.OperatorForemanId || null}
                          assetId={process?.AssetId ?? 0}
                          onChange={(value: number | null) => {
                            saveProcessData({
                              processId: process?.ProcessId
                                ? process?.ProcessId
                                : 0,
                              field: "OperatorForemanId",
                              value,
                            });
                          }}
                          label={"Foreman"}
                          onlyForemans={true}
                          onFocus={pause}
                          onBlur={resume}
                        />
                      </Grid>
                      <Grid item>
                        <ProcessDataTextFieldDE<Process>
                          field={"ForemanFillInName"}
                          label={"Fill In"}
                          value={process?.ForemanFillInName}
                          onChange={(value) => {
                            setProcess({
                              ...process,
                              ["ForemanFillInName"]: value,
                            });
                          }}
                          onBlur={() => {
                            saveProcessData({
                              processId: process?.ProcessId!,
                              field: "ForemanFillInName",
                              value: process!["ForemanFillInName"],
                            }).then(() => {
                              resume();
                            });
                          }}
                          onFocus={pause}
                        />
                      </Grid>
                      <Grid item>
                        <OperatorSelector
                          disableClearable
                          value={process?.OperatorId ?? null}
                          assetId={process?.AssetId ?? 0}
                          onChange={(value: number | null) => {
                            saveProcessData({
                              processId: process?.ProcessId
                                ? process?.ProcessId
                                : 0,
                              field: "OperatorId",
                              value,
                            });
                          }}
                          label={"Operator"}
                          onFocus={pause}
                          onBlur={resume}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid
                    key={`lmf-data-entry_third-${process?.ProcessId}`}
                    item
                    xs={12}
                    sm={6}
                    md={2}
                  >
                    <Grid container direction={"column"} spacing={1}>
                      <Grid item>
                        <LadleSelector
                          disableClearable
                          disabled={process?.HeatId ? false : true}
                          value={process?.LadleId || null}
                          highlight={missingFieldNames.has("LadleId")}
                          onChange={(value: number) => {
                            saveHeat({
                              heatId: process?.HeatId!,
                              field: "LadleId",
                              value,
                            });
                          }}
                          onOpen={pause}
                          onClose={resume}
                        />
                      </Grid>
                      <Grid item container spacing={1}>
                        <Grid item xs={6} md={6}>
                          <DurationInput
                            label="Power On [mm:ss]"
                            mode="mm:ss"
                            initMinutes={
                              process?.PowerOn ? process.PowerOn / 60 : 0
                            }
                            disabled
                          />
                        </Grid>
                        <Grid item xs={6} md={6}>
                          <DurationInput
                            label="Power Off [mm:ss]"
                            mode="mm:ss"
                            initMinutes={
                              process?.PowerOff ? process.PowerOff / 60 : 0
                            }
                            disabled
                          />
                        </Grid>
                      </Grid>
                      <Grid item>
                        <ProcessDataNumberTextField<Process>
                          field={"EnergyConsumption"}
                          label="Energy [kWh]"
                          value={process?.EnergyConsumption}
                          onChange={(value) => {
                            setProcess({
                              ...process,
                              ["EnergyConsumption"]: value,
                            });
                          }}
                          onBlur={() => {
                            saveProcessData({
                              processId: process?.ProcessId!,
                              field: "EnergyConsumption",
                              value: process!["EnergyConsumption"],
                            }).then(() => {
                              resume();
                            });
                          }}
                          onFocus={pause}
                        />
                      </Grid>
                      <Grid
                        item
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          flexWrap: "nowrap",
                          alignItems: "flex-start",
                          gap: 0.5,
                          width: "100%",
                          minHeight: "56px",
                          containerType: "inline-size",
                          "& .MuiFormControlLabel-root": {
                            flex: "1 1 0",
                            minWidth: 0,
                            margin: 0,
                            alignItems: "flex-start",
                          },
                          "& .MuiFormControlLabel-label": {
                            whiteSpace: "normal",
                            overflowWrap: "normal",
                            wordBreak: "normal",
                            hyphens: "none",
                            lineHeight: 1.15,
                            fontSize: "clamp(0.6rem, 5cqi, 0.875rem)",
                          },
                          "& .MuiCheckbox-root": { paddingTop: 0 },
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!process?.IsArriveManual}
                              disabled
                              size="small"
                            />
                          }
                          label="Manual Arrive"
                          slotProps={{ typography: { variant: "body2" } }}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!process?.IsShipManual}
                              disabled
                              size="small"
                            />
                          }
                          label="Manual Ship"
                          slotProps={{ typography: { variant: "body2" } }}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!process?.EAFSlaggedInLadle}
                              size="small"
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setProcess({
                                  ...process,
                                  EAFSlaggedInLadle: checked,
                                });
                                saveProcessData({
                                  processId: process?.ProcessId!,
                                  field: "EAFSlaggedInLadle",
                                  value: checked,
                                });
                              }}
                            />
                          }
                          label="EAF Slagged in Ladle"
                          slotProps={{ typography: { variant: "body2" } }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid
                    key={`lmf-data-entry_buttons-notes-${process?.ProcessId}`}
                    item
                    xs={12}
                    sm={6}
                    md={4}
                  >
                    <Grid
                      container
                      direction="row"
                      justifyContent={"space-between"}
                      spacing={3}
                    >
                      {deProcess?.process && (
                        <ArriveButtons
                          process={deProcess?.process}
                          hasProcessData={
                            (deProcess?.probes?.filter(
                              (p) => p.ProcessCode === "L" && p.Order !== 1,
                            )?.length ?? 0) > 0 ||
                            (deProcess?.heatings?.length ?? 0) > 0 ||
                            (deProcess?.additions?.length ?? 0) > 0
                          }
                          changeHeatStatus={changeHeatStatus}
                          refreshData={refreshData}
                          pause={pause}
                          setShowArrive={setShowArrive}
                          setErrorMessage={setErrorMessage}
                          saveProcess={saveProcess}
                        />
                      )}
                      <Grid
                        key={`lmf-data-entry_forth-${process?.ProcessId}`}
                        item
                        xs={12}
                        md={6}
                      >
                        <Grid container direction={"column"} spacing={2}>
                          <Grid item xs={12} sx={{ mt: 1 }}>
                            <RefreshButtons
                              seconds={seconds}
                              isRunning={isRunning}
                              pause={pause}
                              restart={restart}
                              resume={resume}
                              refreshData={refreshData}
                              isDataLoading={isDataLoading}
                              process={process}
                            />
                          </Grid>
                          <Grid item xs={12} sx={{ mt: 0.5 }}>
                            <Button
                              fullWidth
                              variant={"outlined"}
                              size="medium"
                              onClick={() =>
                                navigate(
                                  `/lmf-processes/${process?.ShiftId}?processId=${process?.ProcessId}`,
                                )
                              }
                            >
                              Processes
                            </Button>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item md={12} sx={{ mt: 2.5 }}>
                      <NotesFieldDE
                        value={process?.Comments ?? ""}
                        onChange={(value) => {
                          setProcess({
                            ...process,
                            Comments: value,
                          });
                        }}
                        onBlur={() => {
                          saveProcessData({
                            processId: process?.ProcessId!,
                            field: "Comments",
                            value: process?.Comments ?? "",
                          }).then(() => {
                            resume();
                          });
                        }}
                        onFocus={pause}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>
          <Grid item container spacing={1}>
            <Grid item xs={12} md={4}>
              <Grid container direction={"column"} spacing={1}>
                <Grid item xs={12} md={12}>
                  <TableProbes
                    key={`lmf-de-table-probes-${process?.ProcessId}`}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={4}>
              <Grid container direction={"column"} spacing={1}>
                <Grid item xs={12} md={12}>
                  <TableAdditions
                    key={`lmf-de-table-additions-${process?.ProcessId}`}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={4}>
              <Grid container direction={"column"} spacing={1}>
                <Grid item xs={12} md={12}>
                  <TableHeatings
                    key={`lmf-de-table-heatings-${process?.ProcessId}`}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item container spacing={1}>
            <Grid item xs={12} md={6}>
              <TablePlugMetrics
                key={`lmf-de-table-plug-metrics-${process?.ProcessId}`}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SteelTemperatureChart probes={deProcess?.probes ?? []} />
            </Grid>
          </Grid>
          <Grid item>
            <TableChemistry
              key={`lmf-data-entry-${deProcess?.process.HeatId}`}
              heatId={deProcess?.process?.HeatId ?? null}
            />
          </Grid>
        </Grid>
      </Box>
      <HeatSelector
        show={showArrive}
        onHide={() => {
          setShowArrive(false);
          resume();
        }}
        assetId={process?.AssetId ?? 0}
        changeHeatStatus={changeHeatStatus}
      />
      <SelectLadleCarModal
        open={showLadleCarModal}
        onHide={() => setShowLadleCarModal(false)}
        currentLadleCar={process?.LadleCar ?? null}
        isArriveManual={!!process?.IsArriveManual}
        onSave={async (selectedCar) => {
          await saveProcessData({
            processId: process?.ProcessId!,
            field: "LadleCar",
            value: selectedCar,
          });
          await readAllPlugsFromPLC({
            ProcessId: process?.ProcessId!,
          });
          await readProcessDataFromPLC({
            ProcessId: process?.ProcessId!,
          });
          await refreshData();
        }}
      />
      <ConfirmationDialogRaw
        title="Confirmation"
        message={showUnlinkModal.message}
        open={showUnlinkModal.show}
        onClose={handleUnlinkHeat}
      />
      <LinkLMFProcess
        show={showLinkModal}
        onHide={() => {
          setShowLinkModal(false);
          resume();
        }}
      />
      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />
      <ErrorModal
        error={errorMessage}
        onHide={() => setErrorMessage("")}
        title="Unexpected error"
      />
      <ShipAlertModal
        open={showShipAlert}
        heatName={process?.HeatName ?? null}
        onShip={async () => {
          setShowShipAlert(false);
          if (process) {
            await saveProcess(
              {
                ...process,
                End: process.PendingShipSignalTime ?? new Date(),
              },
              { isShipManual: false }, //altough a manual action it's still a PLC driven event
            );
          }
        }}
        onDismiss={() => {
          setShowShipAlert(false);
          if (process?.ProcessId) {
            setDismissedProcessId(process.ProcessId);
          }
        }}
      />
    </>
  );
};

export default LMFDataEntryPage;
